// Zod schema + defaults + draft helpers for the Strategic / Buyer List wizard.
// Field-level rules (non-empty mandate, min≤max, numeric, source caps) run through
// RHF `form.handleSubmit` + zodResolver at submit. The CTA is also gated on a
// buyer description and at least one source so an empty paid run cannot start.
import { z } from 'zod';
import type { StrategicForm } from '@/types';
import {
  addSizeRangeIssues,
  addSourceCapIssues,
  optionalNumeric,
} from '@/features/research/formValidation';

export { firstErrorMessage } from '@/features/research/formValidation';

const locationGroupSchema = z.object({
  continent: z.string().optional(),
  country: z.string().optional(),
  region: z.string().optional(),
  state: z.string().optional(),
  city: z.string().optional(),
});

export const strategicSchema = z
  .object({
    businessQuery: z
      .array(
        z.object({ value: z.string().max(5000, 'Description must be 5000 characters or fewer') }),
      )
      .min(1),
    industry: z.string(),
    subIndustry: z.string(),
    primaryActivity: z.enum(['Product', 'Service', 'Both', '']),
    secondaryActivity: z.enum(['Manufacturer', 'Distributor', 'Retailer', 'Not Applicable', '']),
    buyerHorizontal: z.boolean(),
    buyerVertical: z.boolean(),
    buyerAdjacent: z.boolean(),
    useRecommendedBuyer: z.boolean(),
    targetDescription: z.string(),
    targetRevenue: optionalNumeric,
    targetEmployees: optionalNumeric,
    targetLocations: z.array(z.string()),
    size: z.object({
      minRevenue: optionalNumeric,
      maxRevenue: optionalNumeric,
      minEmployees: optionalNumeric,
      maxEmployees: optionalNumeric,
      sizeCriteriaLogic: z.enum(['AND', 'OR']),
    }),
    geography: z.array(locationGroupSchema),
    customInsights: z.object({
      scrapingPath: z.literal('strategic').catch('strategic'),
      questions: z.array(z.object({ value: z.string() })),
    }),
    enrichment: z.object({
      useNews: z.boolean(),
      useApollo: z.boolean(),
      apolloEnrichMode: z.enum(['none', 'contacts', 'company_data', 'all']),
      enableLinkedinEnrichment: z.boolean(),
      ownershipEnrichment: z.boolean(),
      acquisitionEnrichment: z.boolean(),
      blankFieldBackfill: z.boolean(),
      acquisitionTargetReadiness: z.boolean(),
      sellSideMandateReadiness: z.boolean(),
      buyerPropensityScore: z.boolean(),
    }),
    llm: z.object({
      useDefault: z.boolean(),
      providers: z.object({
        openai: z.boolean(),
        anthropic: z.boolean(),
        google: z.boolean(),
      }),
      enableFallback: z.boolean(),
    }),
    additionalSearch: z.object({
      enableApolloSearch: z.boolean(),
      apolloMaxResults: z.string().optional(),
      enableGmapsSearch: z.boolean(),
      gmapsMaxResults: z.string().optional(),
      enableCoresignalSearch: z.boolean(),
      coresignalMaxResults: z.string().optional(),
      enableLinkedinSearch: z.boolean(),
      linkedinMaxResults: z.string().optional(),
      enableFindallSearch: z.boolean(),
      findallMaxResults: z.string().optional(),
    }),
    excludeTerms: z.array(z.string()),
  })
  .superRefine((data, ctx) => {
    if (!data.businessQuery.some((q) => q.value.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['businessQuery', 0, 'value'],
        message: 'Add a buyer description before building.',
      });
    }

    addSizeRangeIssues(ctx, data.size);
    addSourceCapIssues(ctx, data.additionalSearch);

    const angleCount = [data.buyerHorizontal, data.buyerVertical, data.buyerAdjacent].filter(
      Boolean,
    ).length;
    if (angleCount !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['buyerHorizontal'],
        message: 'Pick exactly one buyer angle — Horizontal, Vertical, or Adjacent.',
      });
    }
  });

export const DRAFT_KEY = 'quralyst:draft:strategic';

export const strategicDefaults: StrategicForm = {
  businessQuery: [{ value: '' }],
  industry: '',
  subIndustry: '',
  primaryActivity: '',
  secondaryActivity: '',
  buyerHorizontal: true,
  buyerVertical: false,
  buyerAdjacent: false,
  useRecommendedBuyer: false,
  targetDescription: '',
  targetRevenue: '',
  targetEmployees: '',
  targetLocations: [''],
  excludeTerms: [],
  size: {
    minRevenue: '',
    maxRevenue: '',
    minEmployees: '',
    maxEmployees: '',
    sizeCriteriaLogic: 'AND',
  },
  geography: [{ continent: '', country: '', state: '', city: '' }],
  customInsights: {
    scrapingPath: 'strategic',
    questions: [{ value: '' }],
  },
  enrichment: {
    useNews: false,
    useApollo: false,
    apolloEnrichMode: 'none',
    enableLinkedinEnrichment: false,
    ownershipEnrichment: false,
    acquisitionEnrichment: false,
    blankFieldBackfill: false,
    acquisitionTargetReadiness: false,
    sellSideMandateReadiness: false,
    buyerPropensityScore: false,
  },
  llm: {
    useDefault: true,
    providers: { openai: true, anthropic: true, google: true },
    enableFallback: true,
  },
  additionalSearch: {
    enableApolloSearch: true,
    apolloMaxResults: '50',
    enableGmapsSearch: false,
    gmapsMaxResults: '50',
    enableCoresignalSearch: false,
    coresignalMaxResults: '50',
    enableLinkedinSearch: false,
    linkedinMaxResults: '50',
    enableFindallSearch: false,
    findallMaxResults: '50',
  },
};

/** Merge a (possibly partial / stale) draft over defaults so missing keys never crash RHF. */
function mergeDraft(parsed: Partial<StrategicForm>): StrategicForm {
  return {
    ...strategicDefaults,
    ...parsed,
    excludeTerms: parsed.excludeTerms ?? strategicDefaults.excludeTerms,
    size: { ...strategicDefaults.size, ...parsed.size },
    customInsights: {
      ...strategicDefaults.customInsights,
      ...parsed.customInsights,
      scrapingPath: 'strategic',
    },
    enrichment: { ...strategicDefaults.enrichment, ...parsed.enrichment },
    llm: {
      ...strategicDefaults.llm,
      ...parsed.llm,
      providers: { ...strategicDefaults.llm.providers, ...parsed.llm?.providers },
    },
    additionalSearch: { ...strategicDefaults.additionalSearch, ...parsed.additionalSearch },
    businessQuery:
      parsed.businessQuery && parsed.businessQuery.length
        ? parsed.businessQuery
        : strategicDefaults.businessQuery,
    geography:
      parsed.geography && parsed.geography.length ? parsed.geography : strategicDefaults.geography,
    targetLocations:
      parsed.targetLocations && parsed.targetLocations.length
        ? parsed.targetLocations
        : strategicDefaults.targetLocations,
  };
}

/**
 * One-shot handoff for "Use settings" / Edit criteria.
 * Uses sessionStorage (not sticky localStorage) so a normal browser refresh starts clean.
 */
export function stashDraft(values: unknown): void {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(values));
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* storage full / unavailable — ignore */
  }
}

/** Read + clear a one-shot stashed draft. Returns defaults when none is present. */
export function loadDraft(): StrategicForm {
  try {
    localStorage.removeItem(DRAFT_KEY); // drop legacy sticky drafts that blocked a clean refresh
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return strategicDefaults;
    sessionStorage.removeItem(DRAFT_KEY);
    return mergeDraft(JSON.parse(raw) as Partial<StrategicForm>);
  } catch {
    return strategicDefaults;
  }
}

/** No sticky drafts — kept for call-site compatibility; clears any leftover storage. */
export function saveDraft(_values: StrategicForm): void {
  void _values;
  clearDraft();
}

/** Drop any leftover draft so the next visit starts clean. */
export function clearDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY);
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* storage unavailable — ignore */
  }
}
